/*
 * Copyright (c) 2021 CalAmp Corp. All Rights Reserved
*/
import React from 'react';
import {makeStyles} from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import classnames from 'classnames';
import {translate} from '@ion/core/src/locale/locale';
import {sensorBatteryThreshold, SensorUnits} from '@ion/core/src/types/entities';
import {DateType} from '@ion/core/src/utils/DateUtil';
import {getAssetDetailsContentStyles} from '@ion/dls/src/components/assetdetails/current/assetDetailsContentStyles';
import {themedClassName} from '@ion/dls/src/theme/helpers';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import {useThemeContext} from '@ion/dls/src/theme/useThemeContext';
import {DLSTheme} from '@ion/dls/src/types/types';

export interface SensorListItemExpandedContentProps {
  signal: string;
  timeStamp: Date | string | DateType;
  location: string;
  batteryLevel?: number;
  batteryReading?: string;
  batteryUnits?: SensorUnits | string;
  deviceId?: string;
  itemSelected?: boolean;
}

const useStyles = makeStyles((theme: DLSTheme) => {
  const contentStyles = getAssetDetailsContentStyles();
  return {
    sensorListRow : {
      display        : 'flex',
      justifyContent : 'space-between',
      alignItems     : 'center',
      width          : '100%'
    },
    sensorListLeftCell : {
      ...dlsAlt('typography', 'label3'),
      color        : dlsColor('grey2'),
      borderBottom : 'none'
    },
    sensorListRightCell : {
      ...dlsAlt('typography', 'label2'),
      color          : dlsColor('grey2'),
      letterSpacing  : '0.5px',
      whiteSpace     : 'nowrap',
      display        : 'flex',
      justifyContent : 'flex-end',
      alignItems     : 'center',
      flexDirection  : 'row',
      borderBottom   : 'none'
    },
    batteryLabel : {
      marginLeft : '7px'
    },
    batteryReading : {
      marginRight : '6px'
    },
    // current theme only - the expanded details sit on a white block closed
    // by a grey bottom line, each row a label/value pair without borders ...
    currentTable : {
      backgroundColor : dlsColor('white'),
      borderBottom    : `1px solid ${dlsColor('grey5')}`
    },
    currentSensorListRow : {
      padding : '8px 0'
    },
    currentSensorListLeftCell : {
      ...contentStyles.label,
      padding      : 0,
      borderBottom : 'none'
    },
    currentSensorListRightCell : {
      whiteSpace     : 'nowrap',
      display        : 'flex',
      justifyContent : 'flex-end',
      alignItems     : 'center',
      flexDirection  : 'row',
      padding        : 0,
      borderBottom   : 'none',
      fontFamily     : 'Inter',
      fontWeight     : 400,
      fontSize       : '14px',
      lineHeight     : '20px',
      color          : dlsColor('grey2'),
      // the small table's own last cell padding outranks a single class -
      // without this the values sat 16px short of the row's right edge ...
      '&:last-child' : {
        paddingRight : 0
      }
    }
  };
});

/**
 * Content for the expanded portion of a list item in the tire sensor list.
 * Props are dumb and should be pre-formatted to how they prefer to be displayed.
 * Battery level is a number that is the percent charged.
 *
 * ```typescript
 *   import {SensorListItemExpandedContent} from '@ion/features/src/smarterTrailer';
 *
 *   <SensorListItemExpandedContent signal='Enabled'
 *     timeStamp='10/28/2013 | 4:13 PM'
 *     location='Front Left Tire'
 *     batterLevel={87} />
 * ```
 */
export const SensorListItemExpandedContent = ({
  signal,
  timeStamp,
  location,
  deviceId,
  batteryUnits,
  batteryReading = '',
  itemSelected = false
}: SensorListItemExpandedContentProps) => {
  const classes         = useStyles({itemSelected});
  const {isCurrent}     = useThemeContext();
  const leftCellClass   = themedClassName(classes.sensorListLeftCell, classes.currentSensorListLeftCell, isCurrent);
  const rightCellClass  = themedClassName(classes.sensorListRightCell, classes.currentSensorListRightCell, isCurrent);
  const rowClass        = themedClassName(classes.sensorListRow, classes.currentSensorListRow, isCurrent);

  const maxBatteryValue = sensorBatteryThreshold[`maximum${batteryUnits}`]?.value ||
    translate('common:notAvailable');

  return (
    <Table size='small' className={classnames(isCurrent && classes.currentTable)}>
      <TableBody>
        <TableRow className={rowClass}>
          <TableCell align='left' className={leftCellClass}>
            {translate('common:smarterTrailer.tires.diagnostics.signal')}
          </TableCell>
          <TableCell align='right' className={rightCellClass}>
            {signal}
          </TableCell>
        </TableRow>
        <TableRow className={rowClass}>
          <TableCell align='left' className={leftCellClass}>
            {translate('common:smarterTrailer.tires.diagnostics.timeStamp')}
          </TableCell>
          <TableCell align='right' className={rightCellClass}>
            {timeStamp}
          </TableCell>
        </TableRow>
        <TableRow className={rowClass}>
          <TableCell align='left' className={leftCellClass}>
            {translate('common:smarterTrailer.tires.diagnostics.location')}
          </TableCell>
          <TableCell align='right' className={rightCellClass}>
            {location}
          </TableCell>
        </TableRow>
        <TableRow className={rowClass}>
          <TableCell align='left' className={leftCellClass}>
            {translate('common:smarterTrailer.tires.diagnostics.sensorId')}
          </TableCell>
          <TableCell align='right' className={rightCellClass}>
            {deviceId}
          </TableCell>
        </TableRow>
        <TableRow className={rowClass}>
          <TableCell align='left' className={leftCellClass}>
            {translate('common:smarterTrailer.tires.diagnostics.batteryLabel')}
          </TableCell>
          <TableCell align='right' className={rightCellClass}>
            <span className={classes.batteryReading}>{`${batteryReading} / ${maxBatteryValue} ${batteryUnits}`}</span>
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );
};

export default SensorListItemExpandedContent;
